// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadCustomerOrderAwaitingApproval from "@guestsupply/App/Handlers/OrderApprovalList/LoadCustomerOrderAwaitingApproval";
import updateAwaitingSearchFields from "@guestsupply/App/Handlers/OrderApprovalList/UpdateAwaitingSearchFields";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getOrderAwaitingApprovalsDataView } from "@guestsupply/App/Store/Selectors/OrderApprovalsSelectors";
import { pluralize } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const getOrderAwaitingApprovalsParameter = (state as CustomAppState).xOrderAwaitingApprovalSearchFields
        ?.getOrderAwaitingApprovalsParameter;
    return {
        orderAwaitingApprovalsDataView: getOrderAwaitingApprovalsDataView(
            state as CustomAppState,
            getOrderAwaitingApprovalsParameter,
        ),
        getOrderAwaitingApprovalsParameter,
    };
};
const mapDispatchToProps = {
    updateAwaitingSearchFields,
    loadCustomerOrderAwaitingApproval,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderAwaitingApprovalListTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    customerHeader?: DataTableHeaderProps;
    countHeader?: DataTableHeaderProps;
    customerCell?: DataTableCellBaseProps;
    orderCountCell?: DataTableCellBaseProps;
    orderCountText?: TypographyPresentationProps;
    customerAwaitingText?: TypographyProps;
    titleWrapper?: InjectableCss;
}

export const tableStyles: OrderAwaitingApprovalListTableStyles = {};

const styles = tableStyles;

const OrderAwaitingApprovalListTable = (props: Props) => {
    const { loadCustomerOrderAwaitingApproval, orderAwaitingApprovalsDataView, getOrderAwaitingApprovalsParameter } =
        props;

    useEffect(() => {
        if (!orderAwaitingApprovalsDataView.value && !orderAwaitingApprovalsDataView.isLoading) {
            loadCustomerOrderAwaitingApproval(getOrderAwaitingApprovalsParameter);
        }
    });

    const headerClick = (sortField: string) => {
        const sort = getOrderAwaitingApprovalsParameter.sort === sortField ? `${sortField} DESC` : sortField;
        props.updateAwaitingSearchFields({ sort });
    };

    if (!orderAwaitingApprovalsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (orderAwaitingApprovalsDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>
                    {translate("No order(s) waiting approval for another customer(s).")}
                </Typography>
            </StyledWrapper>
        );
    }

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (props.getOrderAwaitingApprovalsParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getOrderAwaitingApprovalsParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const orderAwaitingApprovalsCount = orderAwaitingApprovalsDataView.value
        ? orderAwaitingApprovalsDataView.pagination
            ? orderAwaitingApprovalsDataView.pagination.totalItemCount
            : 0
        : undefined;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.customerAwaitingText} aria-label={translate("Other Accounts")}>
                    {siteMessage("GUEST_CUSTOMER_ORDER_AWAITING")}
                </Typography>
                <Typography {...styles.orderCountText}>
                    {orderAwaitingApprovalsCount !== undefined &&
                        orderAwaitingApprovalsCount > 0 &&
                        pluralize(orderAwaitingApprovalsCount, translate("order"), { showNumber: true })}
                </Typography>
            </StyledWrapper>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        tight
                        sorted={sorted("customer")}
                        onSortClick={() => headerClick("customer")}
                        {...styles.customerHeader}
                    >
                        {translate("Customer")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted("count")}
                        onSortClick={() => headerClick("count")}
                        {...styles.countHeader}
                    >
                        {translate("Order Count")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {orderAwaitingApprovalsDataView.value.map(({ id, customer, orderCount }) => (
                        <DataTableRow
                            data-test-selector="orderApproval_orderLine"
                            data-test-key={id}
                            key={id}
                            {...styles.dataTableRow}
                        >
                            <DataTableCell {...styles.customerCell}>{customer}</DataTableCell>
                            <DataTableCell {...styles.orderCountCell} data-test-selector="orderApproval_orderCount">
                                {orderCount}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderAwaitingApprovalListTable),
    definition: {
        group: "Order Approval List",
        displayName: "Other Acoounts Awaiting Approval Table",
        allowedContexts: ["OrderApprovalListPage"],
        isSystem: true,
    },
};

export default widgetModule;
